import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  IconButton,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import TicketDetailsSkeleton from "../skeleton/ticketDetailsSkeleton";
import { baseFontStyle, menuItemTextStyle } from "../../tickets/tickets";
import {
  EPICS_DATA,
  PAGE_DROPDOWN_DATA,
} from "Samarth Module/components/incidentCreation/IncidentForm";
import NestedDropdown from "components/common/NestedDropdown/NestedDropdown";

const ViewTicketDetails = (props) => {
  const {
    ticketViewDialog,
    closeTicketViewDialog,
    ticketDetailsLoading,
    userAccess,
    ticketDetails,
    newComment,
    handleOpenRcaDialog,
    handleAddComment,
    handleCommentTextChange,
    handleUpdateTicket,
  } = props;
  dayjs.extend(customParseFormat);
  const [editingField, setEditingField] = useState(null);

  const [editData, setEditData] = useState({
    title: "",
    description: "",
    epic_name: "",
    page_name: "",
    loading: false,
  });

  useEffect(() => {
    if (ticketViewDialog?.ticket) {
      setEditData({
        title: ticketViewDialog.ticket.title || "",
        description: ticketViewDialog.ticket.description || "",
        epic_name: ticketViewDialog.ticket.epic_name || "",
        page_name: ticketViewDialog.ticket.page_name || "",
      });
    }
  }, [ticketViewDialog]);

  const handleEditChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (field) => {
    try {
      setEditData((prev) => ({ ...prev, loading: true }));
      await handleUpdateTicket(
        ticketViewDialog.ticket.ticket_id,
        field,
        editData[field],
      );

      setEditingField(null);
    } catch (e) {
      console.log("updating ticket error", e);
    } finally {
      setEditData((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleClose = () => {
    setEditData({
      title: "",
      description: "",
      epic_name: "",
      page_name: "",
    });
    setEditingField(null);
    closeTicketViewDialog();
  };

  return (
    <Dialog
      open={ticketViewDialog?.open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          ...baseFontStyle,
          fontSize: "1rem",
          fontWeight: 600,
        }}
      >
        Ticket Details
      </DialogTitle>

      <DialogContent>
        {ticketDetailsLoading ? (
          <TicketDetailsSkeleton />
        ) : (
          ticketViewDialog.ticket && (
            <Box sx={{ mt: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "180px 1fr 180px 1fr",
                  rowGap: 1,
                  columnGap: 2,
                  mb: 2,
                }}
              >
                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Ticket ID
                </Typography>
                <Typography sx={menuItemTextStyle}>
                  {ticketViewDialog.ticket?.ticket_key}
                </Typography>
                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Status
                </Typography>
                <Typography sx={menuItemTextStyle}>
                  {ticketViewDialog.ticket?.status}
                </Typography>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Epic Name
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {editingField === "epic_name" ? (
                    <>
                      <Select
                        size="small"
                        sx={{
                          ...baseFontStyle,
                          fontSize: "0.75rem",
                          fontWeight: 400,
                          maxWidth: 120,
                          textOverflow: "ellipsis",
                        }}
                        inputProps={{
                          sx: {
                            ...baseFontStyle,
                            fontSize: "0.75rem",
                            fontWeight: 400,
                          },
                        }}
                        value={editData.epic_name}
                        onChange={(e) =>
                          handleEditChange("epic_name", e.target.value)
                        }
                      >
                        {EPICS_DATA.map((epic) => (
                          <MenuItem
                            key={epic}
                            sx={{ ...menuItemTextStyle }}
                            value={epic}
                          >
                            {epic}
                          </MenuItem>
                        ))}
                      </Select>

                      <Button
                        disabled={editData?.loading}
                        loading={editData?.loading}
                        sx={{ textTransform: "none", ...menuItemTextStyle }}
                        size="small"
                        onClick={() => handleSave("epic_name")}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={menuItemTextStyle}>
                        {ticketViewDialog.ticket?.epic_name || "N/A"}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => setEditingField("epic_name")}
                      >
                        <EditTwoToneIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Category
                </Typography>
                <Typography sx={menuItemTextStyle}>
                  {ticketViewDialog.ticket?.category}
                </Typography>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Page Name
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {editingField === "page_name" ? (
                    <>
                      <NestedDropdown
                        items={PAGE_DROPDOWN_DATA}
                        title=""
                        value={editData.page_name}
                        onSelect={(item) => {
                          handleEditChange("page_name", item.value);
                        }}
                        style={{ width: "50%" }}
                        triggerStyle={{ minHeight: 0, padding: "0px 10px" }}
                        className="incident-form-dropdown"
                      />

                      <Button
                        disabled={editData?.loading}
                        loading={editData?.loading}
                        size="small"
                        sx={{ textTransform: "none", ...menuItemTextStyle }}
                        onClick={() => handleSave("page_name")}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={menuItemTextStyle}>
                        {ticketViewDialog.ticket?.page_name || "N/A"}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => setEditingField("page_name")}
                      >
                        <EditTwoToneIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Raised By
                </Typography>
                <Typography sx={menuItemTextStyle}>
                  {ticketViewDialog.ticket?.reported_by}
                </Typography>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Title
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {editingField === "title" ? (
                    <>
                      <TextField
                        size="small"
                        value={editData.title}
                        sx={{ ...menuItemTextStyle }}
                        onChange={(e) =>
                          handleEditChange("title", e.target.value)
                        }
                      />

                      <Button
                        disabled={editData?.loading}
                        loading={editData?.loading}
                        size="small"
                        sx={{ textTransform: "none", ...menuItemTextStyle }}
                        onClick={() => handleSave("title")}
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography sx={menuItemTextStyle}>
                        {ticketViewDialog.ticket?.title}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => setEditingField("title")}
                      >
                        <EditTwoToneIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </>
                  )}
                </Box>

                <Typography sx={{ ...menuItemTextStyle, fontWeight: 600 }}>
                  Assigned To
                </Typography>
                <Typography sx={menuItemTextStyle}>
                  {ticketViewDialog.ticket?.assigned_to}
                </Typography>
              </Box>

              <Typography
                sx={{
                  ...menuItemTextStyle,
                  mt: 2,
                }}
              >
                <strong>Description</strong>
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mt: 1,
                  backgroundColor: "#fafafa",
                  position: "relative",
                }}
              >
                {editingField === "description" ? (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={editData.description}
                      onChange={(e) =>
                        handleEditChange("description", e.target.value)
                      }
                    />
                    <Box>
                      <Button
                        disabled={editData?.loading}
                        sx={{ mt: 1, ...menuItemTextStyle }}
                        onClick={() => setEditingField(null)}
                      >
                        Cancel
                      </Button>
                      <Button
                        disabled={editData?.loading}
                        loading={editData?.loading}
                        sx={{
                          mt: 1,
                          textTransform: "none",
                          ...menuItemTextStyle,
                        }}
                        onClick={() => handleSave("description")}
                      >
                        Save
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography sx={menuItemTextStyle}>
                      {ticketViewDialog.ticket.description}
                    </Typography>

                    <IconButton
                      size="small"
                      sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                      }}
                      onClick={() => setEditingField("description")}
                    >
                      <EditTwoToneIcon />
                    </IconButton>
                  </>
                )}
              </Paper>

              {!!(
                userAccess?.canAddRCA &&
                ticketViewDialog.ticket.category.toLowerCase() === "incident"
              ) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    sx={{
                      ...menuItemTextStyle,
                      fontWeight: 600,
                    }}
                  >
                    Root Cause Analysis (RCA)
                  </Typography>

                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mt: 1,
                      backgroundColor: "#fafafa",
                      position: "relative",
                    }}
                  >
                    <Typography sx={menuItemTextStyle}>
                      {ticketDetails?.ticket?.rca ||
                        ticketViewDialog?.ticket?.rca ||
                        "No RCA available"}
                    </Typography>

                    {userAccess?.canAddRCA &&
                      ticketViewDialog.ticket.category.toLowerCase() ===
                        "incident" && (
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                          }}
                          onClick={handleOpenRcaDialog}
                        >
                          <EditTwoToneIcon />
                        </IconButton>
                      )}
                  </Paper>
                </>
              )}

              {!!ticketDetails?.efforts?.length && <Divider sx={{ my: 2 }} />}

              {!!ticketDetails?.efforts?.length && (
                <Typography
                  sx={{
                    ...menuItemTextStyle,
                    fontWeight: 600,
                  }}
                >
                  Effort Details
                </Typography>
              )}

              {!!ticketViewDialog?.ticket?.effort_details?.length &&
                ticketViewDialog?.ticket?.effort_details.map(
                  (effort, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1,
                        mt: 1,
                        display: "flex",
                        flexDirection: "row",
                      }}
                    >
                      <Typography sx={menuItemTextStyle}>
                        {effort.type} :{" "}
                      </Typography>

                      <Typography sx={menuItemTextStyle}>
                        {effort.effort} hrs
                      </Typography>
                    </Paper>
                  ),
                )}

              <Divider sx={{ my: 2 }} />

              {/* Comments */}
              <Typography
                sx={{
                  ...menuItemTextStyle,
                  fontWeight: 600,
                }}
              >
                Comments History
              </Typography>

              <Box sx={{ mt: 1 }}>
                {(ticketDetails?.comments || []).length ? (
                  ticketDetails.comments.map((comment, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={menuItemTextStyle}>
                        {comment.comment}
                      </Typography>

                      <Typography
                        sx={{
                          ...menuItemTextStyle,
                          fontSize: "0.65rem",
                          color: "#777",
                        }}
                      >
                        {comment.commented_by} •{" "}
                        {dayjs(comment.commented_on).format(
                          "DD-MMM-YYYY hh:mm A",
                        )}
                      </Typography>
                    </Paper>
                  ))
                ) : (
                  <Typography sx={menuItemTextStyle}>
                    No comments available
                  </Typography>
                )}
              </Box>

              {!!(ticketDetails?.invalid_reasons || []).length && (
                <Divider sx={{ my: 2 }} />
              )}

              {/* Invalid Reasons */}
              {!!(ticketDetails?.invalid_reasons || []).length && (
                <Typography
                  sx={{
                    ...menuItemTextStyle,
                    fontWeight: 600,
                  }}
                >
                  Invalid Reason History
                </Typography>
              )}

              <Box sx={{ mt: 1 }}>
                {!!(ticketDetails?.invalid_reasons || [])?.length &&
                  ticketDetails?.invalid_reasons.map((reason, index) => (
                    <Paper
                      key={index}
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: 1,
                      }}
                    >
                      <Typography sx={menuItemTextStyle}>
                        <strong>{reason?.reason_type}</strong>
                      </Typography>

                      <Typography sx={menuItemTextStyle}>
                        {reason?.reason_text}
                      </Typography>

                      <Typography
                        sx={{
                          ...menuItemTextStyle,
                          fontSize: "0.65rem",
                          color: "#777",
                        }}
                      >
                        {reason?.created_by} • {reason?.created_on}
                      </Typography>
                    </Paper>
                  ))}
              </Box>

              {userAccess?.canAddComment && (
                <>
                  <Divider sx={{ my: 2 }} />

                  {/* Add Comment */}
                  <Typography
                    sx={{
                      ...menuItemTextStyle,
                      fontWeight: 600,
                    }}
                  >
                    Add Comment
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={newComment}
                    onChange={handleCommentTextChange}
                    sx={{
                      mt: 1,
                      mb: 1,
                      "& .MuiInputLabel-root": menuItemTextStyle,
                      "& .MuiInputBase-input": menuItemTextStyle,
                    }}
                    placeholder="Enter comment..."
                  />
                  <Button
                    variant="contained"
                    disabled={newComment.trim() === ""}
                    sx={{
                      ...menuItemTextStyle,
                      textTransform: "none",
                    }}
                    onClick={() =>
                      handleAddComment(ticketViewDialog.ticket.ticket_id)
                    }
                  >
                    Save Comment
                  </Button>
                </>
              )}
            </Box>
          )
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          sx={{
            ...menuItemTextStyle,
            textTransform: "none",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewTicketDetails;
